import React from 'react';
import AppLoader from '../../shared/AppLoader';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import FontPicker from '../Editor/components/FontPicker';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SwipeDownIcon from '@mui/icons-material/SwipeDown';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import CloseIcon from '@mui/icons-material/Close';
import { CHANNEL_TYPES } from './shared/widgetConstants';
import { ColorRow } from './shared/widgetHelpers';
import useWidgetEditor from './shared/useWidgetEditor';
import WidgetEditorShell from './shared/WidgetEditorShell';

export default function ChatWidgetEditor({ site }) {
  const {
    siteId,
    widgetId,
    history,
    queryWidgetType,
    widgetType,
    setWidgetType,
    loading,
    saving,
    previewOpen,
    setPreviewOpen,
    hoveredChannel,
    setHoveredChannel,
    imageLibrary,
    fontLibrary,
    uploading,
    activeTab,
    setActiveTab,
    validationErrors,
    setValidationErrors,
    showErrorDialog,
    setShowErrorDialog,
    rules,
    widget,
    setWidget,
    handleImageUpload,
    handleImageDelete,
    updateField,
    updateSetting,
    addChannel,
    removeChannel,
    updateChannel,
    moveChannel,
    saveWidget,
  } = useWidgetEditor({ site });

  if (loading) return <AppLoader visible={true} />;

  const tabs = [
    { id: 'content', label: 'Content', icon: <ChatBubbleOutlineIcon /> },
    { id: 'channels', label: 'Channels', icon: <MessageIcon /> },
    { id: 'design', label: 'Design', icon: <ColorLensIcon /> },
    { id: 'layout', label: 'Layout', icon: <ViewQuiltIcon /> },
    { id: 'targeting', label: 'Behavior', icon: <TuneIcon /> },
    { id: 'basic', label: 'Settings', icon: <SettingsIcon /> },
  ];

  const previewContent = (
    <>
      {/* Sticky Bar Bottom Preview (Moved out of animated container) */}
      {previewOpen && widget.settings.menu_layout === 'bar_bottom' && (
        <div style={{
          width: '100%',
          background: 'white',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 32px',
          position: 'absolute',
          bottom: '0',
          left: '0',
          zIndex: 2000,
          boxSizing: 'border-box',
          animation: 'slideUp 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '0 0 30%', maxWidth: '30%', overflow: 'hidden', justifyContent: 'center', height: '100%', padding: '8px 0' }}>
            <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#333', lineHeight: '1.2' }}>{widget.settings.heading_text}</div>
            <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.4' }}>{widget.settings.subheading_text}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {widget.settings.channels?.map(channel => (
                <div key={channel.id} style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: CHANNEL_TYPES.find(t => t.value === channel.type)?.color || '#999',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px'
                }}>
                  {CHANNEL_TYPES.find(t => t.value === channel.type)?.icon}
                </div>
              ))}
            </div>
            <div style={{ width: '1px', height: '32px', background: '#eee', margin: '0 8px' }}></div>
            <div style={{ color: '#999', cursor: 'pointer', padding: '0 12px', fontSize: '24px' }} onClick={() => setPreviewOpen(false)}>
              &times;
            </div>
          </div>
        </div>
      )}

      {/* Floating Widget Mockup */}
      <div
        key={widget.settings.animation}
        onClick={() => setPreviewOpen(!previewOpen)}
        style={{
          position: 'absolute',
          bottom: `${widget.settings.offset_bottom ?? 24}px`,
          right: widget.placement === 'bottom-right' ? `${widget.settings.offset_side ?? 24}px` : 'auto',
          left: widget.placement === 'bottom-left' ? `${widget.settings.offset_side ?? 24}px` : 'auto',
          display: (previewOpen && widget.settings.menu_layout === 'bar_bottom') ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: widget.placement === 'bottom-right' ? 'flex-end' : 'flex-start',
          gap: '12px',
          cursor: 'pointer',
          zIndex: 1000,
          animation: widget.settings.animation === 'none' ? 'none' : `hb-${widget.settings.animation || 'slide-up'} 0.5s cubic-bezier(0.4, 0, 0.2, 1) both`
        }}>
        {/* Menu of Channels (for other layouts) */}
        {previewOpen && widget.settings.menu_layout !== 'bar_bottom' && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '300px' : 'auto',
              background: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? 'white' : (widget.settings.menu_layout === 'bar_bubbles' ? 'white' : 'transparent'),
              borderRadius: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '20px' : (widget.settings.menu_layout === 'bar_bubbles' ? '50px' : '0'),
              boxShadow: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout || widget.settings.menu_layout === 'bar_bubbles') ? '0 20px 50px rgba(0,0,0,0.15)' : 'none',
              overflow: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? 'hidden' : 'visible',
              animation: 'slideUp 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
              marginBottom: (widget.settings.menu_layout === 'bar_bubbles') ? '0' : '10px',
              display: 'flex',
              flexDirection: (widget.settings.menu_layout === 'bar_bubbles') ? 'row' : 'column',
              alignItems: 'center',
              padding: widget.settings.menu_layout === 'bar_bubbles' ? '2px 10px' : (widget.settings.menu_layout === 'floating' ? '2px' : '0'),
              gap: widget.settings.menu_layout === 'bar_bubbles' ? '16px' : (widget.settings.menu_layout === 'floating' ? '12px' : '0'),
              position: 'absolute',
              bottom: `${(widget.settings.bubble_size || 68) + 10}px`,
              ...(widget.placement === 'bottom-left' ? { left: widget.settings.menu_layout === 'floating' ? '-10px' : '0' } : { right: widget.settings.menu_layout === 'floating' ? '-10px' : '0' }),
              width: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '300px' : (widget.settings.menu_layout === 'floating' ? '80px' : 'auto'),
              justifyContent: 'flex-start',
              fontFamily: widget.settings.font_family && widget.settings.font_family !== 'inherit'
                ? widget.settings.font_family
                : 'inherit'
            }}>
            {(widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) && (
              <div style={{ background: widget.settings.menu_header_bg_color, padding: '28px 24px', color: widget.settings.menu_header_text_color, width: '100%', boxSizing: 'border-box', borderRadius: '20px 20px 0 0' }}>
                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'inherit', textTransform: 'none', lineHeight: '1.2' }}>{widget.settings.heading_text}</h4>
                <p style={{ margin: '4px 0 0 0', color: 'inherit', opacity: 0.85, fontSize: '14px', lineHeight: '1.2', textTransform: 'none' }}>{widget.settings.subheading_text}</p>
              </div>
            )}
            <div className="hb-preview-channels-list" style={{
              padding: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '12px 0' : '8px 5px',
              boxSizing: 'border-box', width: '100%',
              display: 'flex',
              flexDirection: (widget.settings.menu_layout === 'bar_bubbles') ? 'row' : 'column',
              gap: (widget.settings.menu_layout === 'floating' || !widget.settings.menu_layout) ? '12px' : (widget.settings.menu_layout === 'bar_bubbles' ? '16px' : '0'),
              alignItems: (widget.settings.menu_layout === 'floating' || widget.settings.menu_layout === 'bar_bubbles') ? 'center' : 'stretch',
              maxHeight: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '250px' : (widget.settings.menu_layout === 'floating' ? '40vh' : 'none'),
              overflowY: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout || widget.settings.menu_layout === 'floating') ? 'auto' : 'visible',
              overflowX: (widget.settings.menu_layout === 'bar_bubbles' || widget.settings.menu_layout === 'floating') ? 'auto' : 'hidden',
              scrollbarWidth: 'none'
            }}>
              {widget.settings.channels?.map(channel => (
                <div key={channel.id}
                  onMouseEnter={() => setHoveredChannel(channel.id)}
                  onMouseLeave={() => setHoveredChannel(null)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '14px 24px' : '0',
                    cursor: 'pointer',
                    background: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) && hoveredChannel === channel.id ? '#f8f9fa' : 'transparent',
                    transition: 'all 0.2s',
                    transform: (widget.settings.menu_layout !== 'card' && widget.settings.menu_layout) && hoveredChannel === channel.id ? 'scale(1.1)' : 'scale(1)',
                    position: 'relative'
                  }}>
                  <div style={{
                    width: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '44px' : '60px',
                    height: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) ? '44px' : '60px',
                    transform: (widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) && hoveredChannel === channel.id ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.2s', borderRadius: '50%',
                    background: CHANNEL_TYPES.find(t => t.value === channel.type)?.color || '#999',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px',
                    boxShadow: (widget.settings.menu_layout === 'floating') ? '0 4px 15px rgba(0,0,0,0.1)' : 'none'
                  }}>
                    {CHANNEL_TYPES.find(t => t.value === channel.type)?.icon}
                  </div>
                  {(widget.settings.menu_layout === 'card' || !widget.settings.menu_layout) && (
                    <span style={{ fontWeight: '600', color: '#333', fontSize: '15px' }}>{channel.label || channel.type}</span>
                  )}
                  {/* Tooltip for floating/bar layouts */}
                  {(widget.settings.menu_layout === 'floating' || widget.settings.menu_layout === 'bar_bubbles') && (
                    <div style={{
                      position: 'absolute',
                      ...(widget.settings.menu_layout === 'floating' ? {
                        right: widget.placement === 'bottom-right' ? '70px' : 'auto',
                        left: widget.placement === 'bottom-left' ? '70px' : 'auto',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      } : {
                        top: '-40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }),
                      background: 'rgba(0,0,0,0.8)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      opacity: hoveredChannel === channel.id ? 1 : 0,
                      transition: 'opacity 0.2s',
                      zIndex: 2000
                    }}>
                      {channel.label || channel.type}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Bubble */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: widget.placement === 'bottom-left' ? 'flex-start' : 'flex-end',
          gap: '12px',
          fontFamily: widget.settings.font_family && widget.settings.font_family !== 'inherit'
            ? widget.settings.font_family
            : 'inherit'
        }}>
          {!previewOpen && widget.settings.show_bubble && (
            <div style={{
              background: widget.settings.bubble_bg_color,
              color: widget.settings.bubble_text_color,
              padding: '12px 20px',
              borderRadius: widget.placement === 'bottom-left' ? '24px 24px 24px 4px' : '24px 24px 4px 24px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
              fontSize: '14px',
              border: '1px solid #efefef',
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '280px'
            }}>
              <div style={{ fontWeight: '700', textTransform: 'none', marginBottom: '4px', lineHeight: '1.2' }}>{widget.settings.heading_text}</div>
              <div style={{ opacity: 0.9, fontSize: '13px', marginTop: '0', textTransform: 'none', lineHeight: '1.2' }}>{widget.settings.subheading_text}</div>
            </div>
          )}

          <div style={{
            width: `${widget.settings.bubble_size || 68}px`,
            height: `${widget.settings.bubble_size || 68}px`,
            borderRadius: widget.settings.bubble_radius !== undefined ? `${widget.settings.bubble_radius}px` : '50%',
            background: widget.settings.main_bubble_bg_color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: widget.settings.main_bubble_icon_color,
            boxShadow: `0 8px 25px rgba(0,0,0,0.18), 0 0 15px ${widget.settings.main_bubble_bg_color}40`,
            transition: 'all 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
            transform: previewOpen ? 'rotate(90deg)' : 'none',
            overflow: 'hidden'
          }}>
            {previewOpen ? (
              <CloseIcon style={{ fontSize: `${(widget.settings.bubble_size || 68) * 0.47}px` }} />
            ) : (
              <>
                {widget.settings.custom_icon_url ? (
                  <img src={widget.settings.custom_icon_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <>
                    {widget.settings.bubble_icon === 'chat' && <ChatBubbleOutlineIcon style={{ fontSize: `${(widget.settings.bubble_size || 68) * 0.47}px` }} />}
                    {widget.settings.bubble_icon === 'message' && <MessageIcon style={{ fontSize: `${(widget.settings.bubble_size || 68) * 0.47}px` }} />}
                    {widget.settings.bubble_icon === 'support' && <HeadsetMicIcon style={{ fontSize: `${(widget.settings.bubble_size || 68) * 0.47}px` }} />}
                    {widget.settings.bubble_icon === 'question' && <span style={{ fontSize: `${(widget.settings.bubble_size || 68) * 0.44}px`, fontWeight: 'bold' }}>?</span>}
                  </>
                )}
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );

  const tabContent = (
    <>
      {activeTab === 'basic' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              Widget Internal Name
              <span style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>{widget.name.length}/50</span>
            </div>
            <input
              type="text"
              value={widget.name}
              onChange={e => updateField('name', e.target.value)}
              placeholder="e.g. Sales Support"
              maxLength={50}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0', fontSize: '15px' }}
            />
          </label>
        </div>
      )}

      {activeTab === 'channels' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div
            onClick={addChannel}
            style={{ color: '#ED5A29', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: 'bold' }}
          >
            <AddIcon style={{ fontSize: '16px', marginRight: '4px' }} /> Add Another Channel
          </div>

          {widget.settings.channels?.map((channel, index) => (
            <div key={channel.id} style={{
              padding: '20px',
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              background: '#FFFFFF',
              position: 'relative',
              boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
            }}>
              <div style={{ position: 'absolute', top: '-10px', right: '-10px', display: 'flex', gap: '8px', zIndex: 5 }}>
                {widget.settings.channels.length > 1 && (
                  <>
                    <div
                      onClick={() => moveChannel(index, -1)}
                      style={{ background: '#FFF', color: index === 0 ? '#ccc' : '#555', cursor: index === 0 ? 'default' : 'pointer', width: '28px', height: '28px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #eee' }}
                    >
                      <KeyboardArrowUpIcon style={{ fontSize: '18px' }} />
                    </div>
                    <div
                      onClick={() => moveChannel(index, 1)}
                      style={{ background: '#FFF', color: index === widget.settings.channels.length - 1 ? '#ccc' : '#555', cursor: index === widget.settings.channels.length - 1 ? 'default' : 'pointer', width: '28px', height: '28px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #eee' }}
                    >
                      <KeyboardArrowDownIcon style={{ fontSize: '18px' }} />
                    </div>
                    <div
                      onClick={() => removeChannel(channel.id)}
                      style={{ background: '#FFF', color: '#d32f2f', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #eee' }}
                    >
                      <DeleteOutlineIcon style={{ fontSize: '18px' }} />
                    </div>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#4B5563', marginBottom: '8px', display: 'block' }}>Messaging Channel Type</span>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={channel.type}
                      onChange={e => updateChannel(channel.id, 'type', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 32px 12px 12px',
                        borderRadius: '8px',
                        border: '1px solid #D1D5DB',
                        background: '#fff',
                        appearance: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#111827',
                        cursor: 'pointer',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '18px'
                      }}
                    >
                      {CHANNEL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#4B5563' }}>Button Label</span>
                    <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{channel.label.length}/30</span>
                  </div>
                  <input
                    type="text"
                    placeholder="e.g. Chat with us"
                    value={channel.label}
                    maxLength={30}
                    onChange={e => updateChannel(channel.id, 'label', e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <span style={{ fontSize: '12px', color: '#888', marginBottom: '6px', display: 'block' }}>
                    {channel.type === 'phone' || channel.type === 'whatsapp' || channel.type === 'sms' ? 'Phone Number' :
                     channel.type === 'email' ? 'Email Address' :
                     channel.type === 'link' ? 'URL' : 'Username / Handle'}
                  </span>
                  <input
                    type="text"
                    placeholder={
                      channel.type === 'phone' || channel.type === 'whatsapp' || channel.type === 'sms' ? '+1 234 567 890' :
                      channel.type === 'email' ? 'hello@example.com' :
                      channel.type === 'messenger' ? 'fb_username' :
                      channel.type === 'telegram' ? 'username' :
                      channel.type === 'link' ? 'https://example.com' : '@username'
                    }
                    value={channel.handle}
                    onChange={e => {
                      updateChannel(channel.id, 'handle', e.target.value);
                      if (e.target.value.trim()) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next[channel.id];
                          return next;
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '12px',
                      borderRadius: '8px',
                      border: validationErrors[channel.id] ? '2px solid #d32f2f' : '1px solid #D0D0D0',
                      outline: 'none',
                      background: validationErrors[channel.id] ? '#fff5f5' : 'white'
                    }}
                  />
                  {validationErrors[channel.id] && (
                    <span style={{ fontSize: '11px', color: '#d32f2f', marginTop: '4px', display: 'block', fontWeight: 'bold' }}>
                      {validationErrors[channel.id]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div
            onClick={addChannel}
            style={{
              marginTop: '10px',
              padding: '15px',
              border: '2px dashed #D1D5DB',
              borderRadius: '12px',
              color: '#ED5A29',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
              background: '#fafafa'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = '#ED5A29'}
            onMouseOut={e => e.currentTarget.style.borderColor = '#D1D5DB'}
          >
            <AddIcon style={{ fontSize: '20px', marginRight: '8px' }} /> Add Another Channel
          </div>
        </div>
      )}

      {activeTab === 'content' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Heading</span>
              <span style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>{widget.settings.heading_text?.length || 0}/50</span>
            </div>
            <input
              type="text"
              value={widget.settings.heading_text}
              maxLength={50}
              onChange={e => updateSetting('heading_text', e.target.value)}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0' }}
            />
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Sub-heading</span>
              <span style={{ fontSize: '11px', color: '#999', fontWeight: 'normal' }}>{widget.settings.subheading_text?.length || 0}/100</span>
            </div>
            <input
              type="text"
              value={widget.settings.subheading_text}
              maxLength={100}
              onChange={e => updateSetting('subheading_text', e.target.value)}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0' }}
            />
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '4px 0' }}>
            <div
              onClick={() => {
                updateSetting('show_bubble', !widget.settings.show_bubble);
              }}
              style={{
                width: '40px',
                height: '20px',
                background: widget.settings.show_bubble ? '#ED5A29' : '#D1D5DB',
                borderRadius: '20px',
                position: 'relative',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '16px',
                height: '16px',
                background: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: widget.settings.show_bubble ? '22px' : '2px',
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }} />
            </div>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#4B5563' }}>Show greeting bubble (when widget is closed)</span>
          </label>
        </div>
      )}

      {activeTab === 'design' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FontDownloadIcon style={{ fontSize: '16px', color: '#ED5A29' }} /> Typography
            </span>
            <FontPicker
              customFonts={[
                { label: 'Website Default (Inherit)', value: 'inherit', family: 'inherit' },
                ...fontLibrary.map(f => ({ label: f.font_name, value: f.font_name, family: f.font_name }))
              ]}
              value={widget.settings.font_family || 'inherit'}
              onChange={font => updateSetting('font_family', font)}
            />
            <p style={{ fontSize: '11px', color: '#777', margin: 0 }}>
              Pick a font that matches your website's brand identity.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Theme Colors</span>
            {widget.settings.show_bubble && (
              <ColorRow label="Bubble (Greeting)" colorKey="bubble_bg_color" textKey="bubble_text_color" settings={widget.settings} onChange={updateSetting} />
            )}
            <ColorRow label="Header" colorKey="menu_header_bg_color" textKey="menu_header_text_color" settings={widget.settings} onChange={updateSetting} />
            <ColorRow label="Button" colorKey="main_bubble_bg_color" textKey="main_bubble_icon_color" settings={widget.settings} onChange={updateSetting} textLabel="ICON" />
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          {/* Bubble Style Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Bubble Style</span>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280' }}>Size: {widget.settings.bubble_size ?? 60}px</span>
                <input type="range" min="40" max="100" value={widget.settings.bubble_size ?? 60} onChange={e => updateSetting('bubble_size', parseInt(e.target.value))} style={{ width: '100%', accentColor: '#ED5A29' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280' }}>Radius: {widget.settings.bubble_radius ?? 10}px</span>
                <input type="range" min="0" max="50" value={widget.settings.bubble_radius ?? 10} onChange={e => updateSetting('bubble_radius', parseInt(e.target.value))} style={{ width: '100%', accentColor: '#ED5A29' }} />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Default Icons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase' }}>Default Icons</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  {[
                    { id: 'chat', icon: <ChatBubbleOutlineIcon /> },
                    { id: 'message', icon: <MessageIcon /> },
                    { id: 'support', icon: <HeadsetMicIcon /> },
                    { id: 'question', icon: <span style={{ fontWeight: 'bold' }}>?</span> }
                  ].map(icon => (
                    <div
                      key={icon.id}
                      onClick={() => {
                        updateSetting('bubble_icon', icon.id);
                        updateSetting('custom_icon_url', null);
                      }}
                      style={{
                        height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        border: `2px solid ${widget.settings.bubble_icon === icon.id && !widget.settings.custom_icon_url ? '#ED5A29' : '#F3F4F6'}`,
                        background: widget.settings.bubble_icon === icon.id && !widget.settings.custom_icon_url ? '#ED5A2910' : 'white',
                        color: widget.settings.bubble_icon === icon.id && !widget.settings.custom_icon_url ? '#ED5A29' : '#4B5563',
                        transition: 'all 0.2s'
                      }}
                    >
                      {React.cloneElement(icon.icon, { style: { fontSize: '20px' } })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload & Library */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase' }}>
                  Custom Logo <span style={{ fontWeight: '400', textTransform: 'none', color: '#9CA3AF' }}>(Max 1 MB)</span>
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    width: '100%', height: '50px', background: imageLibrary.length >= 10 ? '#F3F4F6' : '#F9FAFB', border: `2px dashed ${imageLibrary.length >= 10 ? '#E5E7EB' : '#D1D5DB'}`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (uploading || imageLibrary.length >= 10) ? 'not-allowed' : 'pointer', gap: '8px', transition: 'all 0.2s', color: imageLibrary.length >= 10 ? '#9CA3AF' : '#ED5A29', opacity: imageLibrary.length >= 10 ? 0.7 : 1
                  }}>
                    {uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon style={{ fontSize: '20px' }} />}
                    <span style={{ fontSize: '11px', fontWeight: '700' }}>{uploading ? 'UPLOADING...' : 'UPLOAD NEW LOGO'}</span>
                    <input type="file" accept="image/*" hidden onChange={handleImageUpload} disabled={uploading || imageLibrary.length >= 10} />
                  </label>

                  {imageLibrary.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {imageLibrary.map(img => (
                        <div
                          key={img.id}
                          onClick={() => {
                            updateSetting('bubble_icon', 'custom');
                            updateSetting('custom_icon_url', img.url);
                          }}
                          style={{
                            aspectRatio: '1/1', borderRadius: '8px', cursor: 'pointer', position: 'relative', overflow: 'hidden', background: 'white',
                            border: `2px solid ${widget.settings.custom_icon_url === img.url ? '#ED5A29' : '#E5E7EB'}`,
                            transition: 'all 0.2s'
                          }}
                        >
                          <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
                          {widget.settings.custom_icon_url === img.url && (
                            <div style={{ position: 'absolute', top: 2, left: 2, width: '12px', height: '12px', background: '#ED5A29', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ width: '4px', height: '4px', background: 'white', borderRadius: '50%' }}></div>
                            </div>
                          )}
                          <div onClick={(e) => handleImageDelete(e, img.id)} style={{ position: 'absolute', top: 0, right: 0, width: '16px', height: '16px', background: 'rgba(239, 68, 68, 0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', borderBottomLeftRadius: '4px', zIndex: 2 }}>
                            <CloseIcon style={{ fontSize: '10px' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'layout' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Widget Layout</span>
            <select
              value={widget.settings.menu_layout || 'card'}
              onChange={e => updateSetting('menu_layout', e.target.value)}
              style={{ padding: '10px 36px 10px 10px', borderRadius: '6px', border: '1px solid #D0D0D0', background: '#fff', fontSize: '13px' }}
            >
              <option value="card">Slide-in Card</option>
              <option value="floating">Floating Bubbles</option>
              <option value="bar_bubbles">Sticky Bar Bubbles</option>
              <option value="bar_bottom">Sticky Bar Bottom</option>
            </select>
            {widget.settings.menu_layout === 'bar_bottom' && (
              <div style={{ marginTop: '4px', padding: '10px 12px', background: '#F0F7FF', border: '1px solid #BAE6FD', borderRadius: '6px', fontSize: '12px', color: '#0369A1', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '14px', marginTop: '-1px' }}>ⓘ</span>
                <span style={{ lineHeight: '1.4' }}>To guarantee the best experience, this layout automatically converts into space-saving <b>Floating Bubbles</b> on mobile devices.</span>
              </div>
            )}
          </div>

          <div style={{ height: '1px', background: '#F3F4F6' }}></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Position</span>
            <div style={{ display: 'flex', gap: '10px', background: '#F3F4F6', padding: '4px', borderRadius: '10px' }}>
              <button
                onClick={() => updateField('placement', 'bottom-left')}
                style={{
                  flex: 1, padding: '8px', borderRadius: '7px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', border: 'none',
                  background: widget.placement === 'bottom-left' ? 'white' : 'transparent',
                  boxShadow: widget.placement === 'bottom-left' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                  color: widget.placement === 'bottom-left' ? '#ED5A29' : '#6B7280',
                  transition: 'all 0.2s'
                }}
              >
                BOTTOM LEFT
              </button>
              <button
                onClick={() => updateField('placement', 'bottom-right')}
                style={{
                  flex: 1, padding: '8px', borderRadius: '7px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', border: 'none',
                  background: widget.placement === 'bottom-right' ? 'white' : 'transparent',
                  boxShadow: widget.placement === 'bottom-right' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                  color: widget.placement === 'bottom-right' ? '#ED5A29' : '#6B7280',
                  transition: 'all 0.2s'
                }}
              >
                BOTTOM RIGHT
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Bottom Offset (px)</span>
              <input type="number" min="0" max="500" value={widget.settings.offset_bottom ?? 24} onChange={e => updateSetting('offset_bottom', Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #D0D0D0', fontSize: '13px' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Side Offset (px)</span>
              <input type="number" min="0" max="500" value={widget.settings.offset_side ?? 24} onChange={e => updateSetting('offset_side', Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #D0D0D0', fontSize: '13px' }} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Trigger Condition</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {[
                { id: 'immediately', label: 'Immediate', icon: <PlayCircleOutlineIcon style={{ fontSize: '18px' }} /> },
                { id: 'wait', label: 'Time Delay', icon: <ScheduleIcon style={{ fontSize: '18px' }} /> },
                { id: 'scroll', label: 'Scroll %', icon: <SwipeDownIcon style={{ fontSize: '18px' }} /> },
                { id: 'exit-intent', label: 'Exit Intent', icon: <MoveToInboxIcon style={{ fontSize: '18px' }} /> }
              ].map(t => (
                <div
                  key={t.id}
                  onClick={() => updateSetting('view_condition', t.id)}
                  style={{
                    padding: '12px', borderRadius: '10px', border: widget.settings.view_condition === t.id ? '2px solid #ED5A29' : '1px solid #E5E7EB', background: widget.settings.view_condition === t.id ? '#FFF5F2' : 'white', color: widget.settings.view_condition === t.id ? '#ED5A29' : '#4B5563', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s', fontSize: '13px', fontWeight: '600'
                  }}
                >
                  {t.icon} {t.label}
                </div>
              ))}
            </div>
          </div>

          {(widget.settings.view_condition === 'wait' || widget.settings.view_condition === 'scroll') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#F9FAFB', padding: '15px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#6B7280' }}>
                {widget.settings.view_condition === 'wait' ? 'Seconds to Wait' : 'Scroll Depth (%)'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="range"
                  min={widget.settings.view_condition === 'wait' ? 1 : 5}
                  max={widget.settings.view_condition === 'wait' ? 60 : 100}
                  value={widget.settings.view_condition_value || (widget.settings.view_condition === 'wait' ? 5 : 50)}
                  onChange={e => updateSetting('view_condition_value', parseInt(e.target.value))}
                  style={{ flex: 1, accentColor: '#ED5A29' }}
                />
                <div style={{ width: '50px', textAlign: 'center', fontWeight: 'bold', color: '#ED5A29', background: 'white', padding: '4px', borderRadius: '4px', border: '1px solid #E5E7EB', fontSize: '14px' }}>
                  {widget.settings.view_condition_value || (widget.settings.view_condition === 'wait' ? 5 : 50)}{widget.settings.view_condition === 'wait' ? 's' : '%'}
                </div>
              </div>
            </div>
          )}

          <div style={{ height: '1px', background: '#E5E7EB', margin: '5px 0' }}></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Entrance Animation</span>
            <select
              value={widget.settings.animation || 'slide-up'}
              onChange={e => updateSetting('animation', e.target.value)}
              style={{ padding: '10px', borderRadius: '6px', border: '1px solid #D0D0D0', background: '#fff', fontSize: '13px' }}
            >
              <option value="none">None</option>
              <option value="fade">Fade In</option>
              <option value="slide-up">Slide Up</option>
              <option value="bounce">Bounce</option>
            </select>
          </div>

          <div style={{ height: '1px', background: '#E5E7EB', margin: '5px 0' }}></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#4F4F4F' }}>Targeting Rule</span>
            <select
              value={widget.rule_id || ''}
              onChange={e => setWidget(prev => ({ ...prev, rule_id: e.target.value }))}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #D0D0D0', background: '#fff', fontFamily: 'inherit', fontSize: '14px' }}
            >
              {rules.map(rule => (
                <option key={rule.id} value={rule.id}>{rule.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );

  return (
    <WidgetEditorShell
      site={site}
      widgetType={widgetType}
      updateWidgetType={setWidgetType}
      widgetId={widgetId}
      queryWidgetType={queryWidgetType}
      saving={saving}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      onSave={() => saveWidget()}
      onCancel={() => history.push(`/sites/${siteId}/widgets`)}
      tabs={tabs}
      previewContent={previewContent}
      siteId={siteId}
      showErrorDialog={showErrorDialog}
      setShowErrorDialog={setShowErrorDialog}
      validationErrors={validationErrors}
    >
      {tabContent}
    </WidgetEditorShell>
  );
}
